// @flow

import React from 'react';
import { Translate } from 'react-redux-i18n';
import { connect } from 'react-redux';
import { getCurrentPcId } from '../../../utils/urlUtils';
import shoppingCenterApi from '../../../network/api/shoppingCenterApi';
import { branchLevelColor, reconciliationStates } from '../../../const';
import MorphButton from '../../../components/MorphButton';
import type { BranchLevels, ReconciliationStore } from '../../../types/shoppingCenterType';
import VisioGlobeUI from '../../common/VisioGlobeUI';
import type { MapViewer } from '../../../types/VisioglobeType';
import IconButton from '../../../components/IconButton';
import geminiApi from '../../../network/api/geminiApi';
import type { MediaGallery } from '../../../types/keepeekType';
import { LEASING_PLAN } from '../../../utils/keepeekUtils';

type Props = {
  branchLevels: BranchLevels[],
  shoppingCenterMedia: MediaGallery[],
  mapviewer: MapViewer,
  isGeminiUser: boolean,
  changeMappingMode: Function,
  isMappingUser: boolean,
}
type State = {
  reconciliationList: ReconciliationStore[],
  colorSelected: string[],
  search: '',
  activePlace: ?ReconciliationStore,
  openStoreFilter: boolean,
  openBLFilter: boolean,
}

class BaseMapOverlay extends React.Component<Props, State> {
  state: State = {
    search: '',
    colorSelected: [],
    activePlace: null,
    reconciliationList: [],
    openStoreFilter: false,
    openBLFilter: false,
  };

  initMap = (stores) => {
    const { reconciliationList } = this.state;
    const colorMap = {};
    stores.forEach((store) => {
      const reconciliationStore = reconciliationList
        .filter(elt => (
          elt.idMercury === store.storeId || elt.retailerName === store.retailerName
        ))[0];
      if (reconciliationStore && reconciliationStore.idVisioglobes) {
        colorMap[reconciliationStore.idVisioglobes[0]] = branchLevelColor[store.parentBranchId];
      }
    });
    this.props.mapviewer.loadLabelsBaseMap(
      reconciliationList.filter(str => str.state === reconciliationStates.OK
        || str.state === reconciliationStates.MAPPED),
      colorMap,
    );
  };

  selectStoreCallback = (store: ReconciliationStore) =>
    this.setState({ activePlace: store });

  componentDidMount() {
    this.props.mapviewer.setSelectStoreCallback(this.selectStoreCallback);
    geminiApi.fetchReconciliation(getCurrentPcId())
      .then(response => response.json())
      .then((reconciliationList) => {
        this.setState({
          reconciliationList,
        });
        shoppingCenterApi.fetchStores(getCurrentPcId())
          .then(response => response.json())
          .then((stores) => {
            this.initMap(stores);
          });
      }).catch(console.error);
  }

  updateColorFilter = (filter) => {
    let currentFilter = this.state.colorSelected;
    if (currentFilter.includes(branchLevelColor[filter])) {
      currentFilter =
        currentFilter.filter(elt => elt !== branchLevelColor[filter]);
    } else {
      currentFilter.push(branchLevelColor[filter]);
    }
    this.props.mapviewer.updateStoreColors(currentFilter);
    this.setState({ colorSelected: currentFilter });
  };

  clearFilterSearch = () => {
    this.setState({
      search: '',
    });
  };

  clearFilter = () => {
    this.props.mapviewer.updateStoreColors([]);
    this.setState({
      colorSelected: [],
    });
  };

  getLeasingPlanUrl = (): ?string => {
    const leasingPlan = this.props.shoppingCenterMedia.filter(media => media.gallery === LEASING_PLAN);
    if (leasingPlan.length > 0) {
      return leasingPlan[0].medias[0].originalUrl;
    }
    return null;
  };

  render() {
    const leasingPlanUrl = this.getLeasingPlanUrl();

    return (
      <>
        <MorphButton
          isOpened={this.state.openStoreFilter}
          className="search-button search-store-button"
          icon="far fa-search"
          onClick={() => this.setState({
            openStoreFilter: !this.state.openStoreFilter,
            openBLFilter: false,
          })}
          onBlur={() => this.setState({ openStoreFilter: false })}
        >
          <div className="filters">
            <div
              role="presentation"
              onClick={e => e.stopPropagation()}
              className="filter-first-step"
            >
              <div className={`search-input${this.state.search === '' ? ' empty' : ''}`}>
                <input
                  onChange={e => this.setState({ search: e.target.value })}
                  placeholder="&#xF002;"
                />
              </div>
              <div className="filter-container">
                {
                  this.state.reconciliationList
                    .filter(store =>
                      store.idVisioglobes && store.luxName && store.luxName.toLowerCase()
                        .startsWith(this.state.search.toLowerCase()))
                    .sort((a, b) => a.luxName.localeCompare(b.luxName))
                    .map(store => (
                      <button
                        key={store.idVisioglobes[0]}
                        onClick={() => this.props.mapviewer
                          .selectPlace(store.idVisioglobes[0], () => {
                            this.forceUpdate();
                            this.setState({ activePlace: this.props.mapviewer.getActivePlace() });
                          })
                        }
                        className="search-filter"
                      >
                        <div
                          className="filter-title"
                        >
                          {store.luxName}
                        </div>
                      </button>
                    ))
                }
              </div>
              <div>
                <button
                  onClick={this.clearFilterSearch}
                  className="apollo-button"
                >
                  <Translate value="filters.CLEAR_FILTER" />
                </button>
              </div>
            </div>
          </div>
        </MorphButton>
        <MorphButton
          isOpened={this.state.openBLFilter}
          className="search-button bl-button"
          text="BL"
          onClick={() => this.setState({
            openBLFilter: !this.state.openBLFilter,
            openStoreFilter: false,
          })}
          onBlur={() => this.setState({ openBLFilter: false })}
        >
          <div className="filters">
            <div
              role="presentation"
              onClick={e => e.stopPropagation()}
              className="filter-second-step"
            >
              <div className="filter-container">
                {
                  this.props.branchLevels
                    .sort((a, b) => a.branchLevel1Label.localeCompare(b.branchLevel1Label))
                    .map(branchLevel => (
                      <button
                        key={branchLevel.branchLevel1Id}
                        onClick={() => this.updateColorFilter(branchLevel.branchLevel1Id)}
                        className={`search-filter ${this.state.colorSelected.includes(branchLevelColor[branchLevel.branchLevel1Id]) ? 'active' : ''}`}
                      >
                        <div
                          className="filter-title"
                        >
                          {branchLevel.branchLevel1Label}
                        </div>
                        <div
                          className="color-indicator"
                          style={{
                            backgroundColor: branchLevelColor[branchLevel.branchLevel1Id],
                          }}
                        />
                      </button>
                    ))
                }
                <div>
                  <button
                    onClick={this.clearFilter}
                    className="apollo-button"
                  >
                    <Translate value="filters.CLEAR_FILTER" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </MorphButton>
        <IconButton
          className="lp-button"
          text="LP"
          disabled={leasingPlanUrl === null}
          onClick={() => {
            window.open(leasingPlanUrl, '_blank');
          }}
        />
        {
          this.props.isMappingUser
          && (
            <IconButton
              icon="fas fa-repeat-alt"
              className="mapping-mode-button"
              onClick={this.props.changeMappingMode}
            />
          )
        }
        <div className="bottom-menu-gemini-container">
          <div className="bottom-menu-container">
            <VisioGlobeUI mapviewer={this.props.mapviewer} />
          </div>
        </div>
        {
          this.state.activePlace
          && (
            <div className="store-info">
              <div className="store-info--box">
                <Translate value="mallMap.ID_VISIOGLOBE" />
                <span className="store-info--text">{this.state.activePlace.idMercury}</span>
              </div>
              <div className="store-info--box">
                <Translate value="mallMap.RETAILER" />
                <span className="store-info--text">
                  {this.state.activePlace.retailerName || this.state.activePlace.mercuryName || 'No retailer'}
                </span>
              </div>
            </div>
          )}
      </>
    );
  }
}

export default connect(state => ({
  branchLevels: state.shoppingCenterFilters.branchLevelList,
  shoppingCenterMedia: state.shoppingCenterFilters.shoppingCenterMedia,
}))(BaseMapOverlay);
